import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../../services/apiClient';

// The Weekly Schedule, laid out the way QuickBooks Time lays it out (Pat, Sep 24 2026):
// a row per job (or per person), Sunday to Saturday across, hours totals on the row
// and on the day, one grey chip per person per shift. The office and Executives can
// change it here — add, move, retime, remove, publish — and every change lands in
// QuickBooks Time, which the phones and the clock read from.

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const pad = n => String(n).padStart(2, '0');
const ymd = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
function sundayOf(d) { const x = new Date(d); x.setDate(x.getDate() - x.getDay()); x.setHours(0, 0, 0, 0); return x; }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function isoLocal(d) {
  const off = -d.getTimezoneOffset(); const sign = off >= 0 ? '+' : '-';
  return `${ymd(d)}T${pad(d.getHours())}:${pad(d.getMinutes())}:00${sign}${pad(Math.floor(Math.abs(off) / 60))}:${pad(Math.abs(off) % 60)}`;
}
// "7a", "3:30p" — the way the chips read in QuickBooks Time.
function clock(iso) {
  if (!iso) return '';
  const d = new Date(iso); if (isNaN(d)) return '';
  const h = d.getHours(), m = d.getMinutes();
  return `${h % 12 || 12}${m ? ':' + pad(m) : ''}${h < 12 ? 'a' : 'p'}`;
}
const hhmm = iso => { const d = new Date(iso); return isNaN(d) ? '' : `${pad(d.getHours())}:${pad(d.getMinutes())}`; };
const hoursOf = s => (s.allDay || !s.start || !s.end) ? 0 : Math.max(0, (new Date(s.end) - new Date(s.start)) / 3600000);
// "204:00" — hours and minutes, the QuickBooks Time way.
const fmtHrs = h => `${Math.floor(h)}:${pad(Math.round((h - Math.floor(h)) * 60))}`;

const GREY = '#7d7d7d';
const btn = { background: '#e9ebee', border: 'none', borderRadius: 3, padding: '8px 14px', fontSize: 13.5, fontWeight: 600, color: '#2b2f36', cursor: 'pointer', whiteSpace: 'nowrap' };
const eyebrow = { fontSize: 11, fontWeight: 700, letterSpacing: 0.6, color: '#6b7280', textTransform: 'uppercase' };

export default function WeekSchedule({ onBack }) {
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState(() => sundayOf(new Date()));
  const [view, setView] = useState('job'); // 'job' | 'person'
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [people, setPeople] = useState([]);
  const [jobcodes, setJobcodes] = useState([]);
  const [modal, setModal] = useState(null);   // { mode: 'add'|'edit', shift }
  const [menu, setMenu] = useState(false);
  const [bulk, setBulk] = useState(null);     // { kind, preview } for copy / match
  const [busy, setBusy] = useState('');
  const [flash, setFlash] = useState('');

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(anchor, i)), [anchor]);
  const dayKeys = days.map(ymd);
  const todayStr = ymd(new Date());
  const rangeStart = isoLocal(anchor), rangeEnd = isoLocal(addDays(anchor, 7));

  function load() {
    setLoading(true); setError('');
    return apiFetch(`/schedule?start=${encodeURIComponent(rangeStart)}&end=${encodeURIComponent(rangeEnd)}`)
      .then(setData).catch(e => setError(e?.error || 'Could not load the schedule. Check the QuickBooks Time connection.'))
      .finally(() => setLoading(false));
  }
  useEffect(() => { load(); }, [anchor]); // eslint-disable-line react-hooks/exhaustive-deps
  const canEdit = !!data?.canEdit;
  useEffect(() => {
    if (!canEdit || people.length) return;
    apiFetch('/schedule/people').then(p => setPeople(Array.isArray(p) ? p : [])).catch(() => {});
    apiFetch('/schedule/jobcodes').then(j => setJobcodes(Array.isArray(j) ? j : [])).catch(() => {});
  }, [canEdit]); // eslint-disable-line react-hooks/exhaustive-deps

  // One chip per person per shift. A shift with nobody on it is one "unassigned" chip.
  const shifts = useMemo(() => (data?.events || []).filter(e => dayKeys.includes(e.date)).flatMap(e => {
    const base = { eventId: e.id, date: e.date, start: e.start, end: e.end, allDay: e.allDay, draft: e.draft, title: e.title || '',
      jobcodeId: e.jobcodeId, jobLabel: e.jobLabel || 'Unassigned', projectId: e.projectId || null, crewSize: (e.crew || []).length };
    return e.crew?.length ? e.crew.map(c => ({ ...base, personId: String(c.id), personName: c.name })) : [{ ...base, personId: null, personName: 'unassigned' }];
  }), [data, dayKeys.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = useMemo(() => {
    const map = new Map();
    const put = (key, label, extra, s) => {
      if (!map.has(key)) map.set(key, { key, label, byDay: {}, hours: 0, ...extra });
      const r = map.get(key);
      (r.byDay[s.date] = r.byDay[s.date] || []).push(s);
      r.hours += hoursOf(s);
    };
    shifts.forEach(s => {
      if (view === 'person') put(s.personId || '__none__', s.personId ? s.personName : 'Unassigned', { personId: s.personId }, s);
      else if (!s.personId && !s.jobcodeId) put('__none__', 'Unassigned', { jobcodeId: null }, s);
      else put(`job:${s.jobcodeId}`, s.jobLabel, { jobcodeId: s.jobcodeId, projectId: s.projectId }, s);
    });
    const list = [...map.values()].sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
    list.forEach(r => Object.values(r.byDay).forEach(l => l.sort((a, b) => String(a.start).localeCompare(String(b.start)) || a.personName.localeCompare(b.personName))));
    const none = list.findIndex(r => r.key === '__none__');
    if (none > 0) list.unshift(...list.splice(none, 1));
    return list;
  }, [shifts, view]);

  const dayHours = dayKeys.map(dk => shifts.filter(s => s.date === dk).reduce((n, s) => n + hoursOf(s), 0));
  const teamCount = new Set(shifts.filter(s => s.personId).map(s => s.personId)).size;
  const jobCount = new Set(shifts.filter(s => s.jobcodeId).map(s => s.jobcodeId)).size;
  const draftCount = new Set(shifts.filter(s => s.draft).map(s => s.eventId)).size;
  const sameMonth = days[0].getMonth() === days[6].getMonth();
  const weekLabel = sameMonth
    ? `${days[0].toLocaleDateString('en-US', { month: 'short' })} ${days[0].getDate()} - ${days[6].getDate()}, ${days[6].getFullYear()}`
    : `${days[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${days[6].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, ${days[6].getFullYear()}`;

  function say(msg) { setFlash(msg); setTimeout(() => setFlash(''), 3500); }
  async function act(label, fn) {
    setBusy(label); setError('');
    try { await fn(); await load(); } catch (e) { setError(e?.error || e?.message || 'That did not save.'); }
    setBusy('');
  }

  // Drag a chip onto another cell: a new job (by job) or a new person (by person), a new day, or both.
  function onDrop(e, row, dk) {
    e.preventDefault();
    let s; try { s = JSON.parse(e.dataTransfer.getData('text/plain')); } catch { return; }
    if (!s || !canEdit) return;
    const body = { personId: s.personId, personName: s.personName, jobLabel: s.jobLabel, date: dk };
    if (view === 'job') { if (row.jobcodeId == null) return; body.jobcodeId = row.jobcodeId; body.jobLabel = row.label; }
    else body.newPersonId = row.personId || '';
    const unchanged = dk === s.date && (view === 'job' ? String(row.jobcodeId) === String(s.jobcodeId) : String(row.personId || '') === String(s.personId || ''));
    if (unchanged) return;
    act('move', async () => { await apiFetch(`/schedule/shifts/${s.eventId}`, { method: 'PUT', body: JSON.stringify(body) }); say(`Moved ${s.personName}.`); });
  }

  async function publish() {
    await act('publish', async () => {
      const r = await apiFetch('/schedule/publish', { method: 'POST', body: JSON.stringify({ start: rangeStart, end: rangeEnd }) });
      say(`Published ${r.published} shift${r.published === 1 ? '' : 's'}.`);
    });
  }
  async function bulkPreview(kind) {
    setMenu(false); setBusy(kind); setError('');
    try {
      const r = kind === 'copy'
        ? await apiFetch('/schedule/copy-last-week', { method: 'POST', body: JSON.stringify({ sunday: ymd(anchor), confirm: false }) })
        : kind === 'tomorrow'
        ? await apiFetch('/schedule/copy-day', { method: 'POST', body: JSON.stringify({ confirm: false }) })
        : await apiFetch('/schedule/match-monday', { method: 'POST', body: JSON.stringify({ monday: ymd(addDays(anchor, 1)), confirm: false }) });
      setBulk({ kind, preview: r });
    } catch (e) { setError(e?.error || 'Could not work that out.'); }
    setBusy('');
  }
  async function bulkConfirm() {
    const { kind } = bulk; setBulk(null);
    await act(kind, async () => {
      const r = kind === 'copy'
        ? await apiFetch('/schedule/copy-last-week', { method: 'POST', body: JSON.stringify({ sunday: ymd(anchor), confirm: true }) })
        : kind === 'tomorrow'
        ? await apiFetch('/schedule/copy-day', { method: 'POST', body: JSON.stringify({ confirm: true }) })
        : await apiFetch('/schedule/match-monday', { method: 'POST', body: JSON.stringify({ monday: ymd(addDays(anchor, 1)), confirm: true }) });
      say(`${r.totals.created} new shift${r.totals.created === 1 ? '' : 's'}, ${r.totals.moved} moved. New ones are drafts until you publish.`);
    });
  }

  const cellFor = (row, dk) => ({
    date: dk, personId: view === 'person' ? row.personId : null, personName: view === 'person' && row.personId ? row.label : '',
    jobcodeId: view === 'job' ? row.jobcodeId : null, jobLabel: view === 'job' && row.jobcodeId ? row.label : '',
  });

  return (
    <div style={{ fontFamily: 'inherit' }}>
      {/* Top bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', marginBottom: 12 }}>
        <button onClick={onBack} style={btn}>‹ Schedule</button>
        <h1 style={{ fontSize: 20, fontWeight: 800, margin: 0 }}>Schedule</h1>
        {canEdit ? (
          <div style={{ position: 'relative' }}>
            <button onClick={() => setMenu(m => !m)} style={btn}>Actions ▾</button>
            {menu && (
              <div onMouseLeave={() => setMenu(false)} style={{ position: 'absolute', top: '110%', left: 0, zIndex: 20, background: '#fff', border: '1px solid #d1d5db', borderRadius: 4, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', minWidth: 260, padding: 4 }}>
                {[
                  ['＋ Add a shift', () => { setMenu(false); setModal({ mode: 'add', shift: { date: dayKeys.includes(todayStr) ? todayStr : dayKeys[1], start: '07:00', end: '15:30', draft: false } }); }],
                  ['Make tomorrow the same as today', () => bulkPreview('tomorrow')],
                  ['Copy last week onto this week', () => bulkPreview('copy')],
                  ['Make the week match Monday', () => bulkPreview('match')],
                  [`Publish ${draftCount} draft shift${draftCount === 1 ? '' : 's'}`, () => { setMenu(false); publish(); }, draftCount === 0],
                ].map(([label, fn, disabled]) => (
                  <button key={label} onClick={fn} disabled={!!disabled}
                    style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: '9px 12px', fontSize: 13.5, color: disabled ? '#9ca3af' : '#111827', cursor: disabled ? 'default' : 'pointer', borderRadius: 3 }}
                    onMouseEnter={e => { if (!disabled) e.currentTarget.style.background = '#f3f4f6'; }} onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}>
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <span style={{ fontSize: 12, color: '#6b7280' }}>Live from QuickBooks Time · view only for your role</span>
        )}
        <span style={eyebrow}>Jobs <b style={{ color: '#2563eb', fontWeight: 700, textTransform: 'none', letterSpacing: 0 }}>{jobCount}</b></span>
        <span style={eyebrow}>Team members <b style={{ color: '#2563eb', fontWeight: 700, textTransform: 'none', letterSpacing: 0 }}>{teamCount} team members</b></span>
        <span style={{ flex: 1 }} />
        {busy && <span style={{ fontSize: 12.5, color: '#6b7280' }}>Saving…</span>}
        {flash && <span style={{ fontSize: 12.5, color: '#15803d', fontWeight: 700 }}>✓ {flash}</span>}
      </div>

      {/* Week bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
        <button onClick={() => setAnchor(sundayOf(new Date()))} style={btn}>Today</button>
        <button onClick={() => setAnchor(a => addDays(a, -7))} style={{ ...btn, padding: '8px 10px' }} title="Previous week">‹</button>
        <button onClick={() => setAnchor(a => addDays(a, 7))} style={{ ...btn, padding: '8px 10px' }} title="Next week">›</button>
        <span style={{ fontSize: 14.5, fontWeight: 800, color: '#111827', marginLeft: 6 }}>{weekLabel}</span>
        <span style={{ flex: 1 }} />
        <div style={{ display: 'flex', border: '1px solid #2563eb', borderRadius: 3, overflow: 'hidden' }}>
          {[['job', 'By Job'], ['person', 'By Person']].map(([v, label]) => (
            <button key={v} onClick={() => setView(v)}
              style={{ background: view === v ? '#2563eb' : '#fff', color: view === v ? '#fff' : '#2563eb', border: 'none', padding: '7px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
              {label}
            </button>
          ))}
        </div>
        {canEdit && (
          <button onClick={publish} disabled={!draftCount || !!busy}
            style={{ ...btn, background: draftCount ? '#2563eb' : '#e9ebee', color: draftCount ? '#fff' : '#9ca3af', cursor: draftCount ? 'pointer' : 'default' }}>
            {draftCount ? `Publish ${draftCount} draft${draftCount === 1 ? '' : 's'}` : 'Published'}
          </button>
        )}
      </div>

      {error && <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, padding: '10px 14px', color: '#dc2626', fontSize: 14, marginBottom: 10 }}>{error}</div>}
      {loading && !data && <div style={{ color: '#6b7280', padding: '30px 0' }}>Loading the schedule…</div>}

      {data && (
        <div style={{ overflowX: 'auto', border: '1px solid #cfd3d9', background: '#fff' }}>
          <div style={{ minWidth: 1280, display: 'grid', gridTemplateColumns: '210px repeat(7, minmax(150px, 1fr))' }}>
            {/* header */}
            <div style={{ position: 'sticky', left: 0, zIndex: 3, background: '#f4f5f7', borderBottom: '1px solid #cfd3d9', borderRight: '1px solid #cfd3d9', padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={eyebrow}>View by</span>
              <select value={view} onChange={e => setView(e.target.value)} style={{ border: '1px solid #2563eb', borderRadius: 3, padding: '5px 8px', fontSize: 13, background: '#fff', fontFamily: 'inherit' }}>
                <option value="job">Job</option>
                <option value="person">Person</option>
              </select>
            </div>
            {days.map((d, i) => {
              const today = dayKeys[i] === todayStr;
              return (
                <div key={i} style={{ background: today ? '#fbe7a1' : '#f4f5f7', borderBottom: '1px solid #cfd3d9', borderRight: i < 6 ? '1px solid #e2e5ea' : 'none', padding: '9px 8px', textAlign: 'center' }}>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: '#111827' }}>{DOW[i]} {d.getDate()}</div>
                  <div style={{ fontSize: 11.5, color: '#4b5563', fontVariantNumeric: 'tabular-nums' }}>{fmtHrs(dayHours[i])}</div>
                </div>
              );
            })}

            {rows.length === 0 && (
              <div style={{ gridColumn: '1 / -1', padding: 30, textAlign: 'center', color: '#6b7280', fontSize: 14 }}>
                No shifts this week.{canEdit ? ' Use Actions to add one or copy last week.' : ''}
              </div>
            )}

            {rows.map(row => (
              <React.Fragment key={row.key}>
                <div style={{ position: 'sticky', left: 0, zIndex: 2, background: '#fff', borderBottom: '1px solid #cfd3d9', borderRight: '1px solid #cfd3d9', padding: '12px 10px', display: 'flex', alignItems: 'flex-start', gap: 8, minHeight: 64 }}>
                  <button disabled={!row.projectId} onClick={() => row.projectId && navigate(`/admin/new-projects/${row.projectId}`)}
                    title={row.label}
                    style={{ flex: 1, minWidth: 0, background: 'none', border: 'none', padding: 0, textAlign: 'left', cursor: row.projectId ? 'pointer' : 'default', fontSize: 13.5, fontWeight: 600, color: row.key === '__none__' ? '#9ca3af' : '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontFamily: 'inherit' }}>
                    {row.key === '__none__' ? '' : row.label}
                  </button>
                  <span style={{ fontSize: 12, color: '#374151', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{row.key === '__none__' ? '' : fmtHrs(row.hours)}</span>
                </div>
                {dayKeys.map((dk, i) => {
                  const list = row.byDay[dk] || [];
                  const today = dk === todayStr;
                  return (
                    <div key={dk}
                      onDragOver={canEdit ? e => e.preventDefault() : undefined}
                      onDrop={canEdit ? e => onDrop(e, row, dk) : undefined}
                      onDoubleClick={canEdit && (view === 'person' ? row.personId : row.jobcodeId) ? () => setModal({ mode: 'add', shift: { ...cellFor(row, dk), start: '07:00', end: '15:30', draft: false } }) : undefined}
                      title={canEdit ? 'Double-click to add a shift here' : undefined}
                      style={{ background: today ? '#fdf8e6' : '#fff', borderBottom: '1px solid #cfd3d9', borderRight: i < 6 ? '1px solid #e2e5ea' : 'none', padding: '4px 3px', display: 'flex', flexDirection: 'column', gap: 3, minHeight: 64 }}>
                      {list.map(s => (
                        <Chip key={`${s.eventId}-${s.personId || 'u'}`} s={s} view={view} canEdit={canEdit}
                          onOpen={() => canEdit && setModal({ mode: 'edit', shift: { ...s, start: s.allDay ? '07:00' : hhmm(s.start), end: s.allDay ? '15:30' : hhmm(s.end), origPersonId: s.personId } })} />
                      ))}
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {canEdit && data && (
        <div style={{ fontSize: 11.5, color: '#6b7280', marginTop: 8 }}>
          Click a shift to change it. Drag it to another {view === 'job' ? 'job or day' : 'person or day'}. Double-click an empty spot to add one. Dashed shifts are drafts the crew cannot see yet.
        </div>
      )}

      {modal && (
        <ShiftModal mode={modal.mode} shift={modal.shift} people={people} jobcodes={jobcodes}
          onClose={() => setModal(null)}
          onSaved={msg => { setModal(null); say(msg); load(); }} />
      )}

      {bulk && (
        <div onClick={() => setBulk(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 5100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 8, padding: 22, width: 'min(460px, 92vw)', boxShadow: '0 12px 40px rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize: 17, fontWeight: 800, marginBottom: 6 }}>{bulk.kind === 'copy' ? 'Copy last week onto this week' : bulk.kind === 'tomorrow' ? 'Make tomorrow the same as today' : 'Make the week match Monday'}</div>
            <div style={{ fontSize: 14, color: '#374151', lineHeight: 1.5, marginBottom: 14 }}>
              This would add <b>{bulk.preview.totals.created}</b> shift{bulk.preview.totals.created === 1 ? '' : 's'}, move <b>{bulk.preview.totals.moved}</b> and leave <b>{bulk.preview.totals.unchanged}</b> as they are.
              New shifts are drafts until you publish them. Nothing is removed.
              {bulk.kind === 'tomorrow' && bulk.preview.off && bulk.preview.off.length > 0 && (
                <div style={{ marginTop: 8, fontSize: 13, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '7px 10px' }}>
                  Off tomorrow, left out: {bulk.preview.off.map(o => o.name).join(', ')}.
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setBulk(null)} style={btn}>Cancel</button>
              <button onClick={bulkConfirm} disabled={!bulk.preview.totals.created && !bulk.preview.totals.moved}
                style={{ ...btn, background: '#2563eb', color: '#fff' }}>Do it</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Chip({ s, view, canEdit, onOpen }) {
  const who = view === 'person' ? s.jobLabel : s.personName;
  const time = s.allDay ? 'All day' : `${clock(s.start)} - ${clock(s.end)}`;
  return (
    <div draggable={canEdit} onClick={onOpen}
      onDragStart={e => { e.dataTransfer.setData('text/plain', JSON.stringify(s)); e.dataTransfer.effectAllowed = 'move'; }}
      title={`${time} ${s.personName} · ${s.jobLabel}${s.title ? ` · ${s.title}` : ''}${s.crewSize > 1 ? ` · ${s.crewSize} on this shift` : ''}${s.draft ? ' · draft' : ''}`}
      style={{ background: s.draft ? '#fff' : GREY, color: s.draft ? '#4b5563' : '#fff', border: s.draft ? `1.5px dashed ${GREY}` : 'none', borderRadius: 2, padding: '5px 7px', fontSize: 11.5, fontWeight: 700, lineHeight: 1.25, cursor: canEdit ? 'pointer' : 'default', userSelect: 'none', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
      {time} {who}
      {!s.personId && s.title && <div style={{ fontWeight: 700, whiteSpace: 'normal' }}>{s.title}</div>}
    </div>
  );
}

// Add or change one shift. Saving writes straight to QuickBooks Time.
function ShiftModal({ mode, shift, people, jobcodes, onClose, onSaved }) {
  const [f, setF] = useState({ personId: shift.personId || '', jobcodeId: shift.jobcodeId ? String(shift.jobcodeId) : '', date: shift.date, start: shift.start || '07:00', end: shift.end || '15:30', allDay: !!shift.allDay, draft: !!shift.draft, title: shift.title || '' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));
  const inp = { width: '100%', boxSizing: 'border-box', border: '1px solid #cfd3d9', borderRadius: 3, padding: '8px 10px', fontSize: 14, fontFamily: 'inherit', background: '#fff' };
  const lbl = { display: 'block', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 };
  const personName = people.find(p => p.id === f.personId)?.name || shift.personName || '';
  const jobLabel = jobcodes.find(j => j.id === f.jobcodeId)?.name || shift.jobLabel || '';

  async function save() {
    if (!f.date) { setErr('Pick a day.'); return; }
    if (!f.allDay && f.end <= f.start) { setErr('The shift ends before it starts.'); return; }
    setSaving(true); setErr('');
    try {
      if (mode === 'add') {
        await apiFetch('/schedule/shifts', { method: 'POST', body: JSON.stringify({ date: f.date, jobcodeId: f.jobcodeId || null, personId: f.personId || null, start: f.start, end: f.end, allDay: f.allDay, draft: f.draft, title: f.title, personName, jobLabel }) });
        onSaved(`Added ${personName || 'an unassigned shift'} on ${jobLabel || 'the schedule'}.`);
      } else {
        const body = { personId: shift.origPersonId, personName: shift.personName, jobcodeId: f.jobcodeId || 0, jobLabel, date: f.date, start: f.start, end: f.end, allDay: f.allDay, draft: f.draft, title: f.title };
        if ((f.personId || '') !== (shift.origPersonId || '')) body.newPersonId = f.personId || '';
        await apiFetch(`/schedule/shifts/${shift.eventId}`, { method: 'PUT', body: JSON.stringify(body) });
        onSaved('Saved to QuickBooks Time.');
      }
    } catch (e) { setErr(e?.error || 'That did not save.'); }
    setSaving(false);
  }
  async function remove() {
    if (!window.confirm(`Take ${shift.personName} off ${shift.jobLabel} on ${shift.date}?`)) return;
    setSaving(true); setErr('');
    try {
      await apiFetch(`/schedule/shifts/${shift.eventId}/remove`, { method: 'POST', body: JSON.stringify({ personId: shift.origPersonId, personName: shift.personName, jobLabel: shift.jobLabel }) });
      onSaved(`Took ${shift.personName} off ${shift.jobLabel}.`);
    } catch (e) { setErr(e?.error || 'Could not remove it.'); setSaving(false); }
  }

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 5100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 8, padding: 22, width: 'min(480px, 94vw)', boxShadow: '0 12px 40px rgba(0,0,0,0.2)', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ fontSize: 17, fontWeight: 800 }}>{mode === 'add' ? 'Add a shift' : 'Shift'}</div>
          <span style={{ flex: 1 }} />
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, color: '#9ca3af', cursor: 'pointer' }}>×</button>
        </div>
        {mode === 'edit' && shift.crewSize > 1 && (
          <div style={{ fontSize: 12.5, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 4, padding: '7px 10px' }}>
            {shift.crewSize} people share this shift. A change here applies to {shift.personName} only; the others stay where they are.
          </div>
        )}
        <div>
          <label style={lbl}>Person</label>
          <select value={f.personId} onChange={e => set('personId', e.target.value)} style={inp}>
            <option value="">Unassigned (a note on the schedule)</option>
            {people.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div>
          <label style={lbl}>Job</label>
          <select value={f.jobcodeId} onChange={e => set('jobcodeId', e.target.value)} style={inp}>
            <option value="">No job</option>
            {jobcodes.map(j => <option key={j.id} value={j.id}>{j.name}</option>)}
          </select>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr', gap: 10 }}>
          <div><label style={lbl}>Day</label><input type="date" value={f.date} onChange={e => set('date', e.target.value)} style={inp} /></div>
          <div><label style={lbl}>Start</label><input type="time" value={f.start} disabled={f.allDay} onChange={e => set('start', e.target.value)} style={inp} /></div>
          <div><label style={lbl}>End</label><input type="time" value={f.end} disabled={f.allDay} onChange={e => set('end', e.target.value)} style={inp} /></div>
        </div>
        <div style={{ display: 'flex', gap: 18, fontSize: 13.5, color: '#374151' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}><input type="checkbox" checked={f.allDay} onChange={e => set('allDay', e.target.checked)} /> All day</label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}><input type="checkbox" checked={!f.draft} onChange={e => set('draft', !e.target.checked)} /> Published (the crew can see it)</label>
        </div>
        <div>
          <label style={lbl}>Note</label>
          <input value={f.title} onChange={e => set('title', e.target.value)} placeholder="Optional — shows on the shift" style={inp} />
        </div>
        {err && <div style={{ fontSize: 13, color: '#dc2626', fontWeight: 700 }}>{err}</div>}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {mode === 'edit' && <button onClick={remove} disabled={saving} style={{ ...btn, background: '#fff', color: '#dc2626', border: '1px solid #fca5a5' }}>Remove</button>}
          <span style={{ flex: 1 }} />
          <button onClick={onClose} disabled={saving} style={btn}>Cancel</button>
          <button onClick={save} disabled={saving} style={{ ...btn, background: '#2563eb', color: '#fff' }}>{saving ? 'Saving…' : mode === 'add' ? 'Add shift' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}
